import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SurveyResultsFilterNames, IResultLocationFilter } from 'models';
import {
	IFilterChangePayload,
	MAP_ZOOM_TYPE_INDEXES,
} from 'modules/admin-panel/pages/results/models';
import {
	getFromSessionStorage,
	SessionStorageKeyTypes,
} from 'utils/clientStorage';
import {
	calculatedRoundsSelector,
	statisticGroupingTypeSelector,
} from 'modules/admin-panel/pages/results/selectors/dashboard.selectors';
import { setActiveLocation } from 'modules/admin-panel/pages/results/results.actions';
import { useSurveyResultsFilter } from '../useSurveyResultsFilter';

import { setZoomTypeIndex } from '../../modules/admin-panel/pages/results/map.actions';

export const useSetActiveLocation = () => {
	const dispatch = useDispatch();
	const {
		onChangeSurveyResultsFilter,
		surveyFilter,
	} = useSurveyResultsFilter();
	const surveyId = getFromSessionStorage(
		SessionStorageKeyTypes.dashboardSurveyId,
	);
	const rounds = useSelector(calculatedRoundsSelector);
	const groupingType = useSelector(statisticGroupingTypeSelector);

	const boundSetActiveLocation = useCallback(
		(p: IFilterChangePayload) => dispatch(setActiveLocation(p)),
		[dispatch],
	);

	const changeZoomTypeIndex = useCallback(
		(location: IResultLocationFilter) => {
			const { country, city, office } = location;

			let newZoomIndex = MAP_ZOOM_TYPE_INDEXES.worldZoom;
			if (office) {
				newZoomIndex = MAP_ZOOM_TYPE_INDEXES.officeZoom;
			} else if (city) {
				newZoomIndex = MAP_ZOOM_TYPE_INDEXES.cityZoom;
			} else if (country) {
				newZoomIndex = MAP_ZOOM_TYPE_INDEXES.countryZoom;
			}

			dispatch(setZoomTypeIndex(newZoomIndex));
		},
		[dispatch],
	);

	const changeResultsFilter = useCallback(
		(location: IResultLocationFilter, resetRegion?: boolean) => {
			const { country, city, office } = location;
			onChangeSurveyResultsFilter({
				[SurveyResultsFilterNames.country]: country,
				[SurveyResultsFilterNames.city]: city,
				[SurveyResultsFilterNames.office]: office,
			});
			if (resetRegion) {
				onChangeSurveyResultsFilter({
					[SurveyResultsFilterNames.region]: undefined,
				});
			}
		},
		[onChangeSurveyResultsFilter],
	);

	return useCallback(
		(location: IResultLocationFilter, resetRegion?: boolean) => {
			boundSetActiveLocation({
				rounds,
				surveyId,
				...surveyFilter,
				...location,
				region: resetRegion
					? undefined
					: surveyFilter[SurveyResultsFilterNames.region],
				groupingType,
			});

			changeZoomTypeIndex(location);
			changeResultsFilter(location, resetRegion);
		},
		[
			groupingType,
			rounds,
			surveyId,
			surveyFilter,
			boundSetActiveLocation,
			changeResultsFilter,
			changeZoomTypeIndex,
		],
	);
};
